import {
  converter,
  formatHex,
  formatHsl,
  formatRgb,
  inGamut,
  interpolate,
  type Oklch,
  parse,
  toGamut,
} from "culori";

/** A lowercase `#rrggbb` string. Every stored and exported color uses this form. */
export type Hex = `#${string}`;

export interface Lch {
  /** Perceptual lightness, 0..1. */
  l: number;
  /** Chroma, 0..~0.37 inside sRGB. */
  c: number;
  /** Hue angle in degrees, 0..360. Meaningless when chroma is ~0. */
  h: number;
}

const toOklchMode = converter("oklch");
const toRgbMode = converter("rgb");
const isRgbGamut = inGamut("rgb");
const mapToRgbGamut = toGamut("rgb", "oklch");

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isHex(value: unknown): value is Hex {
  return typeof value === "string" && /^#[0-9a-f]{6}$/.test(value);
}

/** Expands and lowercases `#abc`, `abc`, `#AABBCC` to `#aabbcc`; returns null otherwise. */
export function normalizeHex(value: string): Hex | null {
  const m = HEX_RE.exec(value.trim());
  if (!m?.[1]) return null;
  let body = m[1].toLowerCase();
  if (body.length === 3) body = [...body].map((ch) => ch + ch).join("");
  return `#${body}`;
}

/**
 * Parses anything a person might paste: hex (with or without `#`, 3/6/8 digits),
 * `rgb()`, `hsl()`, `oklch()`, `oklab()` or a CSS color name. Out-of-gamut colors are
 * mapped into sRGB. Alpha is dropped.
 */
export function parseColor(input: string): Hex | null {
  const text = input.trim();
  if (!text) return null;
  const hex = normalizeHex(text);
  if (hex) return hex;
  if (/^#?[0-9a-f]{8}$/i.test(text)) return normalizeHex(text.replace(/^#?/, "#").slice(0, 7));
  const parsed = parse(text);
  if (!parsed) return null;
  return gamutHex(parsed);
}

function gamutHex(color: Parameters<typeof mapToRgbGamut>[0]): Hex {
  const mapped = isRgbGamut(color) ? toRgbMode(color) : mapToRgbGamut(color);
  return formatHex(mapped) as Hex;
}

export function toLch(hex: Hex): Lch {
  const c = toOklchMode(hex) as Oklch | undefined;
  if (!c) return { l: 0, c: 0, h: 0 };
  return { l: c.l, c: c.c ?? 0, h: Number.isFinite(c.h) ? (c.h as number) : 0 };
}

/** Converts OKLCH to hex, mapping out-of-gamut colors into sRGB while preserving hue. */
export function fromLch({ l, c, h }: Lch): Hex {
  return gamutHex({ mode: "oklch", l: clamp(l, 0, 1), c: Math.max(0, c), h: wrapHue(h) });
}

export function lchInGamut({ l, c, h }: Lch): boolean {
  return isRgbGamut({ mode: "oklch", l, c, h });
}

/** Mixes two colors in OKLab. `t = 0` returns `a`, `t = 1` returns `b`. */
export function mix(a: Hex, b: Hex, t: number): Hex {
  return gamutHex(interpolate([a, b], "oklab")(clamp(t, 0, 1)));
}

/** Shifts OKLCH lightness by `dl` (−1..1). */
export function shade(hex: Hex, dl: number): Hex {
  const lch = toLch(hex);
  return fromLch({ ...lch, l: lch.l + dl });
}

export type ColorFormat = "hex" | "rgb" | "hsl" | "oklch";

export function formatColor(hex: Hex, format: ColorFormat): string {
  switch (format) {
    case "hex":
      return hex;
    case "rgb":
      return formatRgb(hex) ?? hex;
    case "hsl":
      return formatHsl(hex) ?? hex;
    case "oklch": {
      const { l, c, h } = toLch(hex);
      return `oklch(${round(l * 100, 1)}% ${round(c, 3)} ${round(h, 1)})`;
    }
  }
}

export function hexToRgb(hex: Hex): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(r: number, g: number, b: number): Hex {
  const part = (v: number) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** Euclidean distance in OKLab, scaled so 1.0 is roughly a just-noticeable difference. */
export function distance(a: Hex, b: Hex): number {
  const la = toOklab(a);
  const lb = toOklab(b);
  return Math.hypot(la[0] - lb[0], la[1] - lb[1], la[2] - lb[2]) * 100;
}

const toOklabMode = converter("oklab");
export function toOklab(hex: Hex): [number, number, number] {
  const c = toOklabMode(hex);
  return c ? [c.l, c.a, c.b] : [0, 0, 0];
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export function wrapHue(h: number): number {
  return ((h % 360) + 360) % 360;
}

export function round(v: number, digits = 0): number {
  const f = 10 ** digits;
  return Math.round(v * f) / f;
}
